import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Steps } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getManagers , startBacktest, getStrategyVersions} from '@/api'
import type { ManagerDetail, StartBacktestParams ,FirstStepStrategyVal, StrategyVersion } from '@/types'
import { useFeedback } from '@/utils/antdApp'
import './index.scss'
import FirstStep from './components/FirstStep'
import AIChat from './components/AIChat'
import SecondStep, { createSecondStepDefaultValue, type SecondStepValue } from './components/SecondStep'
import ThirdStep from './components/ThirdStep'
import ChatLayout from '@/Layout/ChatLayout'

const steps = [{
    title: '选择策略',
}, {
    title: '配置参数',
}, {
    title: '启动回测',
}, {
    title: '查看结果',
}]

export default function BacktestPage() {
    const [searchParams] = useSearchParams()
    const managerId = searchParams.get('manager_id')
    const versionId = searchParams.get('version_id')
    const { message } = useFeedback()
    const navigate = useNavigate()
    const [managers, setManagers] = useState<ManagerDetail[]>([])
    const [strategyList, setStrategyList] = useState<StrategyVersion[]>([])
    const [firstStepValue, setFirstStepValue] = useState<FirstStepStrategyVal>({
        manager_id: 0,
        stock_pool_id: null,
        stock_pool_name: '',
        strategy_version: {} as StrategyVersion,
    })
    const [secondStepValue, setSecondStepValue] = useState<SecondStepValue>(createSecondStepDefaultValue())
    const [step, setStep] = useState(0)
    const [loading, setLoading] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [submittedRunId, setSubmittedRunId] = useState('')

    useEffect(() => {
        getManagers()
            .then((list) => {
                setManagers( list?? [] )
            })
            .catch((error) => {
                message.error(error instanceof Error ? error.message : '加载基金经理失败')
            })
    }, [])

    useEffect(() => {
        if(managerId && versionId){
            handleSelectManager(Number(managerId))
            setStep(1)
        }
    }, [managerId, versionId])

    const backtestFormData = useMemo<StartBacktestParams>(() => ({
        manager_id: Number(firstStepValue.manager_id) || 0,
        stock_pool_id: secondStepValue.stock_pool_id || null,
        stock_pool_name: secondStepValue.stock_pool_name || null,
        strategy_version_id: Number(firstStepValue.strategy_version.id) || 0,
        strategy_snapshot: firstStepValue.strategy_version.strategy_snapshot,
        display_name: secondStepValue.display_name,
        benchmark: secondStepValue.benchmark,
        start: secondStepValue.start,
        end: secondStepValue.end,
        freq: secondStepValue.freq,
        cash: Number(secondStepValue.cash || 0),
        max_positions: Number(secondStepValue.max_positions || 0),
        commission: secondStepValue.commission,
        slippage: secondStepValue.slippage,
        position_sizing: secondStepValue.position_sizing,
        signals: secondStepValue.signals,
        market: secondStepValue.market,
        version_no: firstStepValue.strategy_version.version_no,
        version_name: firstStepValue.strategy_version.name,
        pre_market: firstStepValue.strategy_version.market,
        focus_sectors: secondStepValue.focus_sectors,
        constraints: secondStepValue.constraints,
    }), [firstStepValue, secondStepValue])

    const hasPatchValue = <T,>(value: T | null | undefined) =>
        value !== undefined && value !== null && (typeof value !== 'string' || value.trim() !== '')

    const handleSelectManager = (managerId: number) => {
        setLoading(true)
        getStrategyVersions(managerId, versionId ? []:['active', 'archived'] ).then((res) => {
            setStrategyList(res.versions)
            setFirstStepValue(() => ({
                manager_id: managerId,
                stock_pool_name: managers.find((manager) => manager.id === managerId)?.default_stock_pool_name ?? '',
                stock_pool_id: managers.find((manager) => manager.id === managerId)?.default_stock_pool_id ?? null,  
                strategy_version: res.versions.find((version) => version.id === Number(versionId)) ?? {} as StrategyVersion,
            }))
        }).catch((error) => {
            message.error(error instanceof Error ? error.message : '加载策略版本失败')
        }).finally(() => {
            setLoading(false)
        })
    }

    const applyBacktestFormPatch = (patch: Partial<StartBacktestParams>) => {
        if (hasPatchValue(patch.strategy_snapshot)) {
            const strategySnapshot = patch.strategy_snapshot as string
            setFirstStepValue((prev) => ({
                ...prev,
                strategy_version: {
                    ...prev.strategy_version,
                    strategy_snapshot: strategySnapshot,
                },
            }))
        }

        setSecondStepValue((prev) => ({
            ...prev,
            ...(hasPatchValue(patch.display_name) ? { display_name: patch.display_name } : {}),
            ...(hasPatchValue(patch.benchmark) ? { benchmark: patch.benchmark } : {}),
            ...(hasPatchValue(patch.start) ? { start: patch.start } : {}),
            ...(hasPatchValue(patch.end) ? { end: patch.end } : {}),
            ...(hasPatchValue(patch.freq) ? { freq: patch.freq } : {}),
            ...(hasPatchValue(patch.cash) ? { cash: patch.cash } : {}),
            ...(hasPatchValue(patch.max_positions) ? { max_positions: patch.max_positions } : {}),
            ...(hasPatchValue(patch.commission) ? { commission: patch.commission } : {}),
            ...(hasPatchValue(patch.slippage) ? { slippage: patch.slippage } : {}),
            ...(hasPatchValue(patch.position_sizing) ? { position_sizing: patch.position_sizing } : {}),
            ...(hasPatchValue(patch.signals) ? { signals: patch.signals } : {}),
            ...(hasPatchValue(patch.market) ? { market: patch.market } : {}),
            ...(hasPatchValue(patch.pre_market) ? { pre_market: patch.pre_market } : {}),
            ...(hasPatchValue(patch.version_no) ? { version_no: patch.version_no } : {}),
            ...(hasPatchValue(patch.version_name) ? { version_name: patch.version_name } : {}),
            ...(hasPatchValue(patch.focus_sectors) ? { focus_sectors: patch.focus_sectors } : {}),
            ...(hasPatchValue(patch.constraints) ? { constraints: patch.constraints } : {}),
        }))
    }

    const nextButtonLabel = step === 0
        ? '下一步：配置参数'
        : step === 1
            ? '启动回测'
            : step === 2
                ? '下一步：查看结果'
                : '完成'

    const goToBacktestList = useCallback(() => {
        const query = submittedRunId
            ? `?highlight=${encodeURIComponent(submittedRunId)}`
            : ''
        navigate(`/backtests${query}`)
    }, [navigate, submittedRunId])

    const handleNextButtonClick = () => {
        if (step === 0) {
            if(firstStepValue.manager_id && firstStepValue.strategy_version) {
                setSecondStepValue((prev) => ({
                    ...prev,
                    ...firstStepValue.strategy_version,
                    version_name: firstStepValue.strategy_version.name,
                    pre_market: firstStepValue.strategy_version.market,
                    stock_pool_name: secondStepValue.stock_pool_id ? secondStepValue.stock_pool_name : firstStepValue.stock_pool_name,
                    stock_pool_id: secondStepValue.stock_pool_id ? secondStepValue.stock_pool_id : firstStepValue.stock_pool_id,
                }))
            }
            setStep(1)
        } else if (step === 1) {
            void submitBacktest()
        } 
    }

    const submitBacktest = async () => {
        if (submitting) return
        const poolId = Number(backtestFormData.stock_pool_id || 0)
        if (!poolId) {
            message.error('请选择股票池')
            return
        }
        setSubmitting(true)
        try {
            const { run_id } = await startBacktest(backtestFormData as StartBacktestParams)
            setSubmittedRunId(run_id)
            setStep(2)
            message.success('回测已提交，后台执行中')
        } catch (error) {
            if (error instanceof Error) {
                message.error(error.message)
            } else {
                message.error('启动回测失败')
            }
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <>
            {step < 2 && (
                <ChatLayout
                    contentWrapped={false}
                    className="backtest-page"
                    content={
                        <div className="backtest-page_container">
                            <div className="page-header backtest-page__header">
                                <div className="backtest-page__header-left">
                                    <Button icon={<ArrowLeftOutlined />} size='small' className='text-12' onClick={() => navigate('/backtests')}>
                                        回测列表
                                    </Button>
                                    /<span className="backtest-page__title font-weight-700">新建回测</span>
                                </div>

                                <div className="backtest-page__header-actions">
                                    {step > 0 && (
                                        <Button
                                            className='ant-btn-text text-12'
                                            disabled={submitting}
                                            onClick={() => setStep((prev) => Math.max(prev - 1, 0))}
                                        >
                                            <ArrowLeftOutlined /> {steps[step - 1].title}
                                        </Button>
                                    )}
                                    <Button
                                        type="primary"
                                        className='text-12'
                                        loading={submitting}
                                        disabled={(step === 0 && !firstStepValue.strategy_version.id) || submitting}
                                        onClick={handleNextButtonClick}
                                    >
                                        {step === 1 && !submitting && (
                                            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24">
                                                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                                            </svg>
                                        )}
                                        {submitting ? '提交中…' : nextButtonLabel}
                                    </Button>
                                </div>
                            </div>

                            <div className="page-content backtest-page__content">
                                <div className="backtest-page__steps">
                                    <Steps size='small' current={step} items={steps.map((stepItem, index) => ({
                                        title: <span className={index === step ? 'font-weight-700' : undefined}>{stepItem.title}</span>,
                                        icon: index < step ? <span className="font-weight-700">✓</span> : '',
                                    }))} />
                                </div>
                                {
                                    step === 0 && (
                                        <FirstStep
                                            loading={loading}
                                            managers={managers}
                                            strategyList={strategyList}
                                            onSelectManager={handleSelectManager}
                                            value={firstStepValue}
                                            onChange={setFirstStepValue}
                                        />
                                    )
                                }
                                {
                                    step === 1 && (
                                        <SecondStep
                                            value={secondStepValue}
                                            onChange={setSecondStepValue}
                                        />
                                    )
                                }
                            </div>
                        </div>
                    }
                    sider={
                        <AIChat backtestFormData={firstStepValue.manager_id ? backtestFormData : {} as StartBacktestParams} step={step} setBacktestFormData={applyBacktestFormPatch} />
                    }
                />   
            )}
            {
                step === 2 && submittedRunId && (
                    <ThirdStep
                        data={backtestFormData}
                        runId={submittedRunId}
                        onGoToList={goToBacktestList}
                    />
                )
            }
        </>
    )
}
